import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CreateTeamPopup from "../../../components/CreateTeamPopup";
import SelectTeamPopup from "../../../components/SelectTeamPopup";
import { jwtDecode } from "jwt-decode";
import ConfirmationModal from "../../../utils/general/confirmationModal";
import { showToast } from "../../../components/ToastNotification";
import {
  Trash2,
  Users,
  UserPlus,
  Shield,
  MapPin,
  Phone,
  Trophy,
} from "lucide-react";

const TeamsTab = ({ tournament, selectedSubcategory }) => {
  const token = useSelector((state) => state.user.token);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState("");
  const [showCreateTeamPopup, setShowCreateTeamPopup] = useState(false);
  const [showSelectTeamPopup, setShowSelectTeamPopup] = useState(false);
  const [userId, setUserId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams`;
      if (selectedSubcategory) {
        url += `?subcategoryId=${selectedSubcategory.id}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Error al obtener los equipos");

      const data = await response.json();
      const formattedData = Array.isArray(data)
        ? data.map((team) => ({
            id: team.team_id ?? team.id,
            teamName: team.team_name,
            category: team.category,
            groupName: team.group_name,
            city: team.city,
            phone: team.phone,
            pictureUrl: team.picture_url,
            initials: getTeamInitials(team.team_name),
            color: getTeamColor(team.team_name),
          }))
        : [];

      setTeams(formattedData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generar iniciales del equipo (máximo 2 letras)
  const getTeamInitials = (name) => {
    if (!name) return "EQ";
    const words = name.split(" ");
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Generar color basado en el nombre del equipo
  const getTeamColor = (name) => {
    const colors = [
      "bg-emerald-500",
      "bg-blue-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-teal-500",
      "bg-indigo-500",
      "bg-red-500",
      "bg-cyan-500",
      "bg-amber-500",
      "bg-lime-500",
      "bg-rose-500",
    ];

    if (!name) return colors[0];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  useEffect(() => {
    fetchTeams();
  }, [
    tournament.id,
    selectedSubcategory,
    showCreateTeamPopup,
    showSelectTeamPopup,
  ]);

  const handleDeleteClick = (teamId) => {
    setSelectedTeamId(teamId);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams/${selectedTeamId}`,
      );

      if (selectedSubcategory?.id) {
        url.searchParams.append("subcategoryId", selectedSubcategory.id);
      }

      const response = await fetch(url.toString(), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Error al eliminar el equipo.");
      }

      await fetchTeams();
      showToast.success("Equipo eliminado correctamente.");
    } catch (error) {
      showToast.error(error.message || "Hubo un error al eliminar el equipo.");
    } finally {
      setIsModalOpen(false);
      setSelectedTeamId(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsModalOpen(false);
    setSelectedTeamId(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
        <p className="font-medium">Error al cargar equipos</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Users className="text-emerald-500" size={24} />
              Equipos Inscritos
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {teams.length} equipo{teams.length !== 1 ? "s" : ""} registrado
              {teams.length !== 1 ? "s" : ""}
              {selectedSubcategory && (
                <span className="ml-1 text-emerald-600 font-medium">
                  en{" "}
                  {selectedSubcategory.name ||
                    selectedSubcategory.category_name}
                </span>
              )}
            </p>
          </div>

          {userId === tournament.user_id && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateTeamPopup(true)}
                className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-emerald-600 transition-all hover:shadow-lg hover:shadow-emerald-500/20 active:scale-95"
              >
                <UserPlus size={18} />
                <span className="hidden sm:inline">Crear Equipo</span>
              </button>
              <button
                onClick={() => setShowSelectTeamPopup(true)}
                className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-blue-600 transition-all hover:shadow-lg hover:shadow-blue-500/20 active:scale-95"
              >
                <Users size={18} />
                <span className="hidden sm:inline">Registrar Equipo</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contenido */}
      {teams.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="text-gray-400" size={40} />
          </div>
          <p className="text-gray-500 text-lg font-medium">
            No hay equipos inscritos aún
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {userId === tournament.user_id
              ? "Crea o registra un equipo para comenzar"
              : "Los equipos aparecerán aquí cuando se registren"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {teams.map((team) => (
            <Link
              to={`/teams/${team.id}`}
              key={team.id}
              className="group relative bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-emerald-200 transition-all duration-300 overflow-hidden"
            >
              {/* Botón eliminar */}
              {userId === tournament.user_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDeleteClick(team.id);
                  }}
                  className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-white shadow-md text-red-500 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all duration-200"
                  title="Eliminar equipo"
                >
                  <Trash2 size={16} />
                </button>
              )}

              {/* Avatar con iniciales o imagen */}
              <div className="flex flex-col items-center p-4">
                {team.pictureUrl ? (
                  <img
                    src={team.pictureUrl}
                    alt={team.teamName}
                    className="w-20 h-20 rounded-full object-cover border-4 border-gray-100 group-hover:border-emerald-200 transition-all duration-300"
                  />
                ) : (
                  <div
                    className={`w-20 h-20 rounded-full ${team.color} flex items-center justify-center border-4 border-gray-100 group-hover:border-emerald-200 transition-all duration-300 shadow-md`}
                  >
                    <span className="text-white text-2xl font-bold">
                      {team.initials}
                    </span>
                  </div>
                )}

                {/* Nombre del equipo */}
                <h3 className="mt-3 font-semibold text-gray-800 text-center text-sm leading-tight group-hover:text-emerald-600 transition-colors">
                  {team.teamName}
                </h3>

                {/* Categoría y grupo */}
                {(team.category || team.groupName) && (
                  <div className="mt-1 flex flex-wrap justify-center gap-1">
                    {team.category && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        {team.category}
                      </span>
                    )}
                    {team.groupName && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {team.groupName}
                      </span>
                    )}
                  </div>
                )}

                {/* Detalles */}
                <div className="mt-3 w-full space-y-1.5 pt-3 border-t border-gray-100">
                  {team.city && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <MapPin
                        size={12}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span className="truncate">{team.city}</span>
                    </div>
                  )}
                  {team.phone && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Phone
                        size={12}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span className="truncate">{team.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Efecto hover inferior */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
            </Link>
          ))}
        </div>
      )}

      {/* Popups */}
      {showCreateTeamPopup && (
        <CreateTeamPopup
          tournament={tournament}
          selectedSubcategoryId={selectedSubcategory?.id}
          onClose={() => setShowCreateTeamPopup(false)}
          onTeamCreated={fetchTeams}
        />
      )}

      {showSelectTeamPopup && (
        <SelectTeamPopup
          tournament={tournament}
          selectedSubcategoryId={selectedSubcategory?.id}
          onClose={() => setShowSelectTeamPopup(false)}
          onTeamRegistered={fetchTeams}
        />
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        message="¿Estás seguro de que deseas eliminar este equipo?"
      />
    </div>
  );
};

export default TeamsTab;
